import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  LayoutDashboard,
  ClipboardList,
  Calendar,
  FileText,
  DollarSign,
  Users,
  Heart,
  Download,
  Clock,
  Settings,
  LogOut,
  Menu,
  X,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'

export function AppShell() {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  async function handleSignOut() {
    await signOut()
    navigate('/login')
  }

  const navItems = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/shifts', label: 'Shifts', icon: ClipboardList },
    { to: '/calendar', label: 'Calendar', icon: Calendar },
    { to: '/evidence', label: 'Evidence', icon: FileText },
    { to: '/pay', label: 'Pay', icon: DollarSign },
    { to: '/waiting-period', label: 'Waiting period', icon: Clock },
    { to: '/recipients', label: 'Recipients', icon: Heart },
    { to: '/sharing', label: 'Sharing', icon: Users },
    { to: '/export', label: 'Export', icon: Download },
    { to: '/settings', label: 'Settings', icon: Settings },
  ]

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F7FAFC' }}>
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        style={{
          position: 'fixed',
          top: '12px',
          left: '12px',
          zIndex: 30,
          padding: '8px',
          background: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '6px',
          display: 'none',
        }}
        className="mobile-menu-btn"
        aria-label="Toggle menu"
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside style={{
        width: collapsed ? '64px' : '220px',
        background: '#FFFFFF',
        borderRight: '1px solid #E2E8F0',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        position: 'sticky',
        top: 0,
        height: '100vh',
      }}>
        <div style={{
          padding: collapsed ? '20px 12px' : '20px',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minHeight: '60px',
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            background: '#2C7A7B',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FFFFFF',
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '16px',
            fontWeight: 500,
            flexShrink: 0,
          }}>K</div>
          {!collapsed && (
            <span style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '17px',
              fontWeight: 500,
              color: '#1A202C',
            }}>KindredCare</span>
          )}
        </div>

        <nav style={{ flex: 1, padding: '12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '9px 10px',
                borderRadius: '6px',
                color: isActive ? '#005440' : '#4A5568',
                background: isActive ? '#E1F5EE' : 'transparent',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: isActive ? 500 : 400,
                justifyContent: collapsed ? 'center' : 'flex-start',
              })}
              title={collapsed ? label : undefined}
            >
              <Icon size={18} style={{ flexShrink: 0 }} />
              {!collapsed && label}
            </NavLink>
          ))}
        </nav>

        <div style={{ padding: '12px', borderTop: '1px solid #E2E8F0' }}>
          {!collapsed && profile && (
            <div style={{ padding: '8px 10px', marginBottom: '4px' }}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#1A202C' }}>{profile.name}</div>
              <div style={{ fontSize: '11px', color: '#718096' }}>
                {profile.role === 'family_caregiver' ? 'Family caregiver' : 'Paid aide'}
              </div>
            </div>
          )}
          <button
            onClick={handleSignOut}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '9px 10px',
              borderRadius: '6px',
              color: '#4A5568',
              fontSize: '14px',
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
            title={collapsed ? 'Sign out' : undefined}
          >
            <LogOut size={18} style={{ flexShrink: 0 }} />
            {!collapsed && 'Sign out'}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, minWidth: 0 }}>
        {profile?.in_waiting_period && <WaitingPeriodBanner />}
        <Outlet />
      </main>

      <style>{`
        @media (max-width: 768px) {
          .mobile-menu-btn { display: block !important; }
        }
      `}</style>
    </div>
  )
}

function WaitingPeriodBanner() {
  return (
    <NavLink
      to="/waiting-period"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 2rem',
        background: '#FCF5E8',
        borderBottom: '1px solid #F4E4C4',
        color: '#8b5000',
        fontSize: '13px',
        textDecoration: 'none',
        fontWeight: 500,
      }}
    >
      <Clock size={14} />
      <span>Waiting Period Mode is active</span>
      <span style={{ color: '#B18A47', fontWeight: 400, marginLeft: 'auto', fontSize: '12px' }}>
        Click to view details →
      </span>
    </NavLink>
  )
}
